/**
 * @author Mark Limm
 * @desc component for filtering by sourceType and/or distributor
 * @example <checkbox-filter-multi-level
              filter-set="filterSet"
            >
            </checkbox-filter-multi-level>
*/
import template from './checkboxFilterMultiLevel.html';

class CheckboxFilterMultiLevelController {
  constructor(
    distributorService,
    $scope
  ) {
    this.distributorService = distributorService;
    this.$scope = $scope;
  }

  $onInit = async () => {
    const [optionsMap, distMap] = await Promise.all([
      this.distributorService.getDistributorsByCategory(),
      this.distributorService.getByType('distributors')
    ]);

    this.optionsMap = optionsMap;
    this.distMap = distMap;

    this.categoryList = this.optionsMap.list;

    this.$scope.$on('$locationChangeSuccess', () => {
      this.setCategoriesAndDistributors();
    });

    this.setCategoriesAndDistributors();
  }

  addRemoveMultipleDistributors = (
    allSelectedDistributorIds,
    distributorsWithinCategory,
    selectedDistributorsFromModal
  ) => {
    const distributorIdsWithinCategory = distributorsWithinCategory.map(e => e.id);

    const selectedDistributorIdsNotInCurrCategory =
      allSelectedDistributorIds.filter(distId => !distributorIdsWithinCategory.includes(distId));

    const selectedDistributorIdsFromModal = selectedDistributorsFromModal.map(e => e.id);

    const result = [
      ...selectedDistributorIdsNotInCurrCategory,
      ...selectedDistributorIdsFromModal
    ];

    return result;
  }

  addRemoveSingleDistributor = (sdi, item) => {
    let selectedDistributorIds = sdi;

    if (item.active) {
      selectedDistributorIds = [item.id].concat(selectedDistributorIds);
    } else {
      selectedDistributorIds = selectedDistributorIds.filter(id => id !== item.id);
    }

    selectedDistributorIds = [...new Set(selectedDistributorIds)];

    return selectedDistributorIds;
  }

  addRemoveSourceTypes() {
    //  note: the "minus"/indeterminate checkbox state is considered to be an "active" state 
    //  (in addition to when the checkbox is checked), which is why we need to also check 
    //  for !this.categoryHasASelectedDistributor(c)
    const sourceTypesInSearchFilter =
      this.categoryList.filter(c => c.active && !this.categoryHasASelectedDistributor(c));

    if (sourceTypesInSearchFilter.length && sourceTypesInSearchFilter.length <
      this.categoryList.length) {
      //  there are 1 or more entire sourceTypes/categories being searched for

      return sourceTypesInSearchFilter.map(c => c.id);
    }

    //  no sourceTypes specified in search (meaning either all or none of the source types 
    //  should be searched on), however there may or may not be distributorIds selected
    return null;
  }

  categoryClicked({ item }) {
    const allSelectedDistributorIds = this.getSelectedDistributorIds();

    const updatedFilters = {
      distributorId:
        this.addRemoveMultipleDistributors(allSelectedDistributorIds, item.children, []),
      sourceType: this.addRemoveSourceTypes()
    };

    this.updateSearch(updatedFilters);
  }

  categoryHasASelectedDistributor = item => item.children && !!item.children.find(e => e.active)

  distributorClicked({ item }) {
    const selectedDistributorIds = this.getSelectedDistributorIds();

    const updatedFilters = {
      distributorId: this.addRemoveSingleDistributor(selectedDistributorIds, item),
      sourceType: this.addRemoveSourceTypes()
    };

    this.updateSearch(updatedFilters);
  }

  distributorsSelected({ category, selectedDistributors }) {
    const allSelectedDistributorIds = this.getSelectedDistributorIds();

    const currCategory = this.categoryList.find(c => c.id === category.id);
    currCategory.children =
      this.getUpdatedSelectedDistributorsFromModal(currCategory, selectedDistributors);

    const updatedFilters = {
      distributorId: this.addRemoveMultipleDistributors(
        allSelectedDistributorIds,
        category.children,
        selectedDistributors
      ),
      sourceType: this.addRemoveSourceTypes()
    };

    this.updateSearch(updatedFilters);
  }

  getSelectedDistributorIds() {
    const distributorFilterValue = this.filterSet.getFilter('distributorId').value || [];
    const selectedDistributorIds = [].concat(distributorFilterValue);

    return selectedDistributorIds;
  }

  getUpdatedSelectedDistributorsFromModal = (currCategory, selectedDistributors) => {
    const selectedDistributorIds = selectedDistributors.map(d => d.id);

    return currCategory.children.map(d => ({
      ...d,
      active: selectedDistributorIds.includes(d.id)
    }));
  }

  setCategoriesAndDistributors() {
    const sourceTypeFilterValue = this.filterSet.getFilter('sourceType').value;
    const selectedDistributorIds = this.getSelectedDistributorIds();

    this.categoryList = this.categoryList.map((item) => {
      let activeValue = false;

      if (!sourceTypeFilterValue || !sourceTypeFilterValue.length) {
        if (selectedDistributorIds.length === 0 || this.categoryHasASelectedDistributor(item)) {
          activeValue = true;
        }
      } else {
        const filterValue = [].concat(sourceTypeFilterValue);
        activeValue = filterValue.indexOf(item.id) > -1;
      }

      return {
        ...item,
        active: activeValue,
        children:
          this.setDistributors(this.optionsMap.map.get(item.id), selectedDistributorIds)
      };
    });
  }

  setDistributors = (distributorsArray, selectedDistributorIds) =>
    distributorsArray.map(item => ({
      ...item,
      active: selectedDistributorIds.indexOf(item.id) > -1
    }));

  showAll() {
    this.updateSearch({
      sourceType: null,
      distributorId: []
    });
  }

  showOne({ item, type }) {
    if (type === 'category') {
      this.showOneCategory(item);
    } else if (type === 'distributor') {
      this.showOneDistributor(item);
    }
  }

  showOneCategory(category) {
    this.updateSearch({
      sourceType: [category.id],
      distributorId: []
    });
  }

  showOneDistributor(distributor) {
    this.updateSearch({
      sourceType: [],
      distributorId: [distributor.id]
    });
  }

  unselectAllDistributors = (distributors, exceptDistributorId) => distributors.map(item => ({
    ...item,
    active: item.id === exceptDistributorId
  }));

  updateSearch({ distributorId, sourceType }) {
    let distributorName = [];
    if (Array.isArray(distributorId) && distributorId.length > 0) {
      distributorName = distributorId.reduce((acc, id) => {
        if (this.distMap.has(id)) {
          acc.push(this.distMap.get(id).name);
        }
        return acc;
      }, []);
    }

    this.filterSet.changeValues({ distributorId, distributorName, sourceType });
  }
}

CheckboxFilterMultiLevelController.$inject = ['distributorService', '$scope'];

const checkboxFilterMultiLevel = {
  controller: CheckboxFilterMultiLevelController,
  template,
  bindings: {
    filterSet: '<'
  }
};

export default function (ngModule) {
  ngModule.component('checkboxFilterMultiLevel', checkboxFilterMultiLevel);
  ngModule.controller('checkboxFilterMultiLevelController', CheckboxFilterMultiLevelController);
}
